import React, { useState } from 'react'
import PropTypes from 'prop-types'
import withApiData from '../../HOC/withApiData'
import { postToLoggingService } from '../../../actions/logging'
import LoadingSpinner from '../../mini/LoadingSpinner'
import MentorStudentFilter from './MentorStudentFilter'
import Chart from './Chart'
import {
  fetchLearningSkillResponses,
  fetchWorkSkillResponses,
  fetchLearningSkillMastery,
  fetchWorkSkillMastery,
} from './API'
import {
  ChartHeader,
  SkillMeaning,
  PaginationWrapper,
  PaginationDots,
  PaginationDot,
  PaginationArrow
} from './_Styled'
import FrameworkSkill from '../../mini/Skills/FrameworkSkill'
import { useQuery } from 'react-query'
import useActiveMasterCourseIds from '../../../hooks/useActiveMasterCourseIds'
import { H3Alternate } from '../../styled/typography/typography'
import WorkSkill from '../../mini/Skills/WorkSkill'

const SkillCard = ({
  apiData,
  dispatch,
  isMentor,
  student,
  skill,
  isWorkSkill,
  isFetchingCourses,
  selectedMasterCourseId,
  autoOpenUcqId,
  nSkills,
  selectedSkillCardIndex,
  setSelectedSkillCardIndex,
  prevSkill,
  nextSkill
}) => {
  const [viewingMentorChecks, setViewingMentorChecks] = useState(true)
  const [viewingStudentChecks, setViewingStudentChecks] = useState(true)
  const activeMasterCourseIds = useActiveMasterCourseIds()
  const filteredMasterCourseIds = selectedMasterCourseId !== -1
    ? [selectedMasterCourseId]
    : (isMentor || isWorkSkill) ? undefined : activeMasterCourseIds

  const logViewResponseAll = () => {
    const data = {
      action: 'view_response_all',
      student: isMentor ? {
        id: student.id,
        name: student.name
      } : null,
      skill: {
        id: skill.id,
        name: skill.name
      },
      is_work_skill: isWorkSkill,
      is_student_responses: viewingStudentChecks,
      is_mentor_responses: viewingMentorChecks,
      skill_framework_version: 2
    }

    dispatch(postToLoggingService('skill_check', data))
  }

  const fetchResponses = isWorkSkill
    ? fetchWorkSkillResponses
    : fetchLearningSkillResponses
  
  const fetchMastery = isWorkSkill
    ? fetchWorkSkillMastery
    : fetchLearningSkillMastery

  const parseMastery = m => m && (
    isWorkSkill
      ? m.artifact_criteria_count > 0
      : m.assessor_assessments_count > 0
  )
    ? parseFloat(isWorkSkill
        ? m.artifact_criteria_score
        : m.current_assessor_mastery)
    : null

  const { data: responses, isFetching } = useQuery(
    [
      'skills-responses',
      {
        studentId: student.id,
        masterCourseId: selectedMasterCourseId,
        skillId: skill.id,
        isWorkSkill
      }
    ],
    () => fetchResponses(
      apiData,
      student,
      skill.id,
      filteredMasterCourseIds
    ),
    {
      refetchOnWindowFocus: false,
      onSuccess: logViewResponseAll
    }
  )

  const { data: mastery } = useQuery(
    [
      'skill-mastery',
      {
        studentId: student.id,
        masterCourseId: selectedMasterCourseId,
        skillId: skill.id,
        isWorkSkill
      }
    ],
    () => fetchMastery(
      apiData,
      student,
      skill.id,
      filteredMasterCourseIds
    ),
    { refetchOnWindowFocus: false }
  )

  const getResponses = () => {
    let resps = {
      student: [],
      mentor: [],
      all: responses
    }

    if (isWorkSkill) {
      resps.mentor = responses.map(r => ({
        ...r,
        rating: r.response_arr.find(
          resp => resp.artifact_assessment_criterium_id === skill.id
        )?.rating
      }))
    } else {
      if (viewingStudentChecks) {
        resps.student = responses.filter(
          r => r.self_assessment === true
        )
      }

      if (viewingMentorChecks) {
        resps.mentor = responses.filter(
          r => r.self_assessment === false
        )
      }
    }

    return resps
  }

  const onStudentFilterClick = () => {
    if (viewingMentorChecks) {
      setViewingStudentChecks(vsc => !vsc)
    }
  }

  const onMentorFilterClick = () => {
    if (viewingStudentChecks) {
      setViewingMentorChecks(vmc => !vmc)
    }
  }

  return skill && !isFetching && !isFetchingCourses ? (
    <div>
      <ChartHeader>
        <div style={{ width: '400px' }}>
          {isWorkSkill ? (
            <WorkSkill
              workSkill={skill}
              as={H3Alternate}
              iconSize="24px"
            />
          ) : (
            <FrameworkSkill
              frameworkSkillId={skill.id}
              isHeader
            />
          )}
          <SkillMeaning>
            {isWorkSkill ? skill.description : skill.meaning}
          </SkillMeaning>
        </div>

        {nSkills > 1 &&
          <PaginationWrapper>
            <PaginationArrow onClick={prevSkill} />
            <PaginationDots>
              {Array(nSkills).fill(0).map((_, index) => (
                <PaginationDot
                  selected={selectedSkillCardIndex === index}
                  onClick={() => setSelectedSkillCardIndex(index)}
                />
              ))}
            </PaginationDots>
            <PaginationArrow right onClick={nextSkill} />
          </PaginationWrapper>
        }
      </ChartHeader>

      <Chart
        dispatch={dispatch}
        student={student}
        skill={skill}
        isWorkSkill={isWorkSkill}
        isMentor={isMentor}
        responses={getResponses()}
        mastery={parseMastery(mastery)}
        autoOpenUcqId={autoOpenUcqId}
      />

      <MentorStudentFilter
        isMentor={isMentor}
        noStudentOption={isWorkSkill}
        viewingMentorChecks={viewingMentorChecks}
        viewingStudentChecks={viewingStudentChecks}
        onMentorChecksClick={onMentorFilterClick}
        onStudentChecksClick={onStudentFilterClick}
      />
    </div>
  ) : (
    <LoadingSpinner />
  )
}

SkillCard.propTypes = {
  dispatch: PropTypes.func.isRequired,
  isMentor: PropTypes.bool,
  student: PropTypes.object.isRequired,
  skill: PropTypes.object.isRequired,
  isWorkSkill: PropTypes.bool,
  isFetchingCourses: PropTypes.bool,
  selectedMasterCourseId: PropTypes.object.isRequired,
  setSelectedMasterCourseId: PropTypes.func.isRequired,
  academicYears: PropTypes.array.isRequired,
  autoOpenUcqId: PropTypes.number
}

export default withApiData(SkillCard)
